import { s } from '../../theme/tokens';
import { Box } from '../primitives/Box';
import type { SpacingToken } from '../../theme/tokens';
import type { BoxProps } from '../primitives/Box';

type GridItemSize = number | 'auto' | 'grow' | false;

export type GridProps = Omit<BoxProps, 'gap'> & { gap?: SpacingToken };

export const LayoutGrid = ({ gap = 'md', children, sx, ...rest }: GridProps): JSX.Element => (
  <Box
    sx={[
      {
        display: 'grid',
        gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
        gap: s(gap),
        minWidth: 0,
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...rest}
  >
    {children}
  </Box>
);

export type GridItemProps = Omit<BoxProps, 'size' | 'gap'> & {
  xs?: GridItemSize;
  sm?: GridItemSize;
  md?: GridItemSize;
  lg?: GridItemSize;
  xl?: GridItemSize;
};

export const LayoutGridItem = ({
  children,
  xs,
  sm,
  md,
  lg,
  xl,
  sx,
  ...rest
}: GridItemProps): JSX.Element => (
  <Box
    sx={[
      {
        gridColumn: buildGridColumnValue(xs),
        minWidth: 0,
        display: xs === false ? 'none' : undefined,
        ...(sm !== undefined ? { '@media (min-width:600px)': buildGridColumnSx(sm) } : {}),
        ...(md !== undefined ? { '@media (min-width:900px)': buildGridColumnSx(md) } : {}),
        ...(lg !== undefined ? { '@media (min-width:1200px)': buildGridColumnSx(lg) } : {}),
        ...(xl !== undefined ? { '@media (min-width:1536px)': buildGridColumnSx(xl) } : {}),
      },
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ]}
    {...rest}
  >
    {children}
  </Box>
);

const buildGridColumnValue = (size: GridItemSize | undefined): string | undefined => {
  if (size === undefined) return undefined;
  if (size === false) return undefined;
  if (size === 'auto') return 'auto';
  if (size === 'grow') return 'auto / span 12';
  return `span ${String(size)}`;
};

const buildGridColumnSx = (size: GridItemSize): { display?: string; gridColumn?: string } => ({
  display: size === false ? 'none' : undefined,
  gridColumn: buildGridColumnValue(size),
});
